import { Node, mergeAttributes } from '@tiptap/core'
import {
  ReactNodeViewRenderer,
  type ReactNodeViewProps,
  NodeViewWrapper,
} from '@tiptap/react'

import { cn } from '../../../../lib/utils'
import { ToolText } from '../ui/tool'
import { Variable as VariablePrimitive } from '../ui/variable'

// Variable component that will be rendered in the editor
const VariableComponent = ({
  node,
  deleteNode,
  selected,
}: ReactNodeViewProps<HTMLSpanElement>) => {
  return (
    <NodeViewWrapper
      className={cn(
        'focus-visible:outline-hidden inline-flex select-none rounded-lg focus-visible:ring-2 focus-visible:ring-primary',
        selected && 'ring-2 ring-primary',
      )}
    >
      {node.attrs.type === 'tool' ? (
        <ToolText>
          {node.attrs.label}
          <button
            className="hover:text-red-500 ml-2 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              deleteNode()
            }}
          >
            ×
          </button>
        </ToolText>
      ) : (
        <VariablePrimitive>
          {node.attrs.label}
          <button
            className="hover:text-red-500 ml-2 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              deleteNode()
            }}
          >
            ×
          </button>
        </VariablePrimitive>
      )}
    </NodeViewWrapper>
  )
}

// Custom Variable extension that extends Mention
export const Variable = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-id': attributes.id,
          }
        },
      },
      type: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-type'),
        renderHTML: (attributes) => {
          if (!attributes.type) {
            return {}
          }
          return {
            'data-type': attributes.type,
          }
        },
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {}
          }
          return {
            'data-label': attributes.label,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="variable"]',
      },
      {
        tag: 'span[data-type="tool"]',
      },
    ]
  },

  renderText({ node }) {
    return `{{${node.attrs.label}|${node.attrs.id}}}`
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-type': HTMLAttributes['data-type'] || 'variable' },
        HTMLAttributes,
      ),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableComponent)
  },
})
